import { useEffect, useRef, useState, type ReactNode } from "react"

import {
  Badge,
  Button,
  cn,
  Dialog,
  DialogBody,
  DialogHeader,
  DialogTitle,
  Loader,
  SearchInput,
} from "@coval/ui"
import { X } from "lucide-react"

import { PickerCatalogContent } from "@/components/entity-picker/PickerCatalogContent"

import { useAddMetricCatalog } from "../hooks/useAddMetricCatalog"
import { AddMetricCard } from "./AddMetricCard"

interface MetricCatalogBrowserProps {
  addLabel?: string
  doneLabel?: string
  presentation?: "dialog" | "picker-panel"
  isOpen: boolean
  isAdding?: boolean
  onClose: () => void
  createContent?: ReactNode
  /** Copy one centrally managed catalog entry into the current workspace. */
  onAdd: (entryId: string, presetKey?: string) => Promise<void> | void
}

export function MetricCatalogBrowser({
  isOpen,
  isAdding = false,
  onClose,
  createContent,
  onAdd,
  addLabel,
  doneLabel = "Done",
  presentation = "dialog",
}: MetricCatalogBrowserProps) {
  const isPickerPanel = presentation === "picker-panel"
  const [search, setSearch] = useState("")
  const [sessionAddCount, setSessionAddCount] = useState(0)
  const [sessionAddedEntryIds, setSessionAddedEntryIds] = useState<Set<string>>(
    () => new Set(),
  )
  const sessionGenerationRef = useRef(0)
  const { drafts, error, groups, hasData, isLoading, isEmpty, retry } =
    useAddMetricCatalog(search)

  useEffect(() => {
    if (isOpen) {
      void retry()
    }
  }, [isOpen, retry])

  useEffect(
    () => () => {
      sessionGenerationRef.current += 1
    },
    [],
  )

  const handleAdd = async (entryId: string, presetKey?: string) => {
    const sessionGeneration = sessionGenerationRef.current
    await (presetKey === undefined ? onAdd(entryId) : onAdd(entryId, presetKey))
    if (sessionGeneration !== sessionGenerationRef.current) {
      return
    }
    setSessionAddedEntryIds((entryIds) => new Set(entryIds).add(entryId))
    setSessionAddCount((count) => count + 1)
  }

  const handleClose = () => {
    sessionGenerationRef.current += 1
    setSearch("")
    setSessionAddCount(0)
    setSessionAddedEntryIds(new Set())
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <PickerCatalogContent
        presentation={presentation}
        className="h-[84vh] w-full max-w-4xl"
      >
        <DialogHeader className="border-b border-coval-border pb-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <DialogTitle className="text-base">
              {isPickerPanel ? "Metric Catalog" : "Add a metric"}
            </DialogTitle>
            <div className="flex items-center gap-2">
              <SearchInput
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                onClear={() => setSearch("")}
                placeholder="Search metrics…"
                aria-label="Search metric catalog"
                className="w-48 sm:w-64"
              />
              <Button
                variant="secondary"
                icon
                onClick={handleClose}
                tooltip="Close"
              >
                <X />
              </Button>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="min-h-0 overflow-y-auto p-6">
          <fieldset disabled={isAdding} className="min-w-0">
            {createContent}

            <div className="my-5 flex items-center gap-3">
              <span className="text-[10px] uppercase tracking-wider text-coval-text-muted">
                Catalog
              </span>
              <hr className="flex-1 border-t border-coval-border" />
            </div>

            {error && (
              <div
                className="mb-4 flex items-center justify-between gap-3 rounded-md border border-coval-border bg-surface-default px-3 py-2"
                role="alert"
              >
                <span className="text-sm text-coval-text-muted">
                  {hasData
                    ? "Couldn't refresh the metric catalog."
                    : "Couldn't load the metric catalog."}
                </span>
                <Button
                  variant="secondary"
                  className="h-7 px-2.5 text-xs"
                  onClick={() => void retry()}
                >
                  Retry
                </Button>
              </div>
            )}

            {isLoading && !hasData && !error && (
              <div className="flex justify-center py-10">
                <Loader />
              </div>
            )}

            {hasData && isEmpty && (
              <div className="py-8 text-center text-sm text-coval-text-muted">
                {search.trim()
                  ? `No catalog metrics match “${search.trim()}”`
                  : "No catalog metrics yet."}
              </div>
            )}

            <div className="space-y-6">
              {drafts.length > 0 && (
                <section
                  aria-labelledby="draft-metric-catalog-heading"
                  className="rounded-md border border-coval-border bg-surface-1 p-3"
                >
                  <div className="mb-2.5 flex items-start justify-between gap-3">
                    <div>
                      <h3
                        className="text-sm font-semibold text-coval-text"
                        id="draft-metric-catalog-heading"
                      >
                        Drafts
                      </h3>
                      <p className="mt-0.5 text-xs text-coval-text-muted">
                        Inactive catalog entries available for staging testing.
                      </p>
                    </div>
                    <Badge variant="outline">{drafts.length}</Badge>
                  </div>
                  <div
                    className={cn(
                      "grid grid-cols-1 gap-2 lg:grid-cols-2",
                      isPickerPanel && "md:grid-cols-2",
                    )}
                  >
                    {drafts.map((item) => (
                      <AddMetricCard
                        key={item.entry_id}
                        isAdded={sessionAddedEntryIds.has(item.entry_id)}
                        item={item}
                        onAdd={handleAdd}
                        disabled={isAdding}
                        addLabel={addLabel}
                      />
                    ))}
                  </div>
                </section>
              )}

              {groups.map((group) => (
                <section key={group.category}>
                  <div className="mb-2.5 flex items-baseline gap-2">
                    <h3 className="text-sm font-semibold text-coval-text">
                      {group.category}
                    </h3>
                  </div>
                  <div
                    className={cn(
                      "grid grid-cols-1 gap-2 lg:grid-cols-2",
                      isPickerPanel && "md:grid-cols-2",
                    )}
                  >
                    {group.items.map((item) => (
                      <AddMetricCard
                        key={item.entry_id}
                        isAdded={sessionAddedEntryIds.has(item.entry_id)}
                        item={item}
                        onAdd={handleAdd}
                        disabled={isAdding}
                        addLabel={addLabel}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </fieldset>
        </DialogBody>

        <div className="flex items-center gap-3 border-t border-coval-border px-6 py-3">
          <span className="text-xs text-coval-text-muted">
            {sessionAddCount === 0
              ? "Nothing added yet"
              : `${sessionAddCount} metric${sessionAddCount > 1 ? "s" : ""} added`}
          </span>
          <div className="flex-1" />
          <Button onClick={handleClose}>{doneLabel}</Button>
        </div>
      </PickerCatalogContent>
    </Dialog>
  )
}
